import { RequestBuilder } from "../requestBuilder";
import {
  AddChiefSessionChatBodySchema,
  ChiefSessionIdParamsSchema,
  ChiefSessionLocationSchema,
  ChiefSessionNameResponseSchema,
  ChiefSessionSchema,
  ChiefSessionShareBodySchema,
  ChiefSessionShareParamsSchema,
  ChiefSessionShareStatusSchema,
  ChiefShareEmailsBodySchema,
  ChiefShareEmailsResponseSchema,
  ChiefShareTokenParamsSchema,
  ChiefTaggedItemsResponseSchema,
  DeviceIdParamsSchema,
  ListChiefSessionMediaResponseSchema,
  ListChiefSessionsParamsSchema,
  ListChiefSessionsResponseSchema,
  ListChiefTagsResponseSchema,
  MoveChiefSessionsBodySchema,
  MoveChiefSessionsResponseSchema,
  PairedDeviceSchema,
  PairedDevicesListSchema,
  PairingIntentIdParamsSchema,
  PairingIntentSchema,
  PublicChiefArtifactImagesBodySchema,
  PublicChiefArtifactImagesResponseSchema,
  PublicChiefArtifactSchema,
  PublicChiefSessionSchema,
  PublishChiefSessionMediaBodySchema,
  PublishChiefSessionMediaResponseSchema,
  RevokedDeviceSchema,
  StartForNotionPageBodySchema,
  StartForNotionPageResponseSchema,
  SuggestChiefSessionPromptsBodySchema,
  SuggestChiefSessionPromptsResponseSchema,
  UpdateChiefSessionNameBodySchema,
} from "./chiefClient.types";

export const GenerateDeviceCodeRequestConfig = new RequestBuilder({
  path: () => "/chief/device-code",
  responseSchema: PairedDeviceSchema,
}).withMethod("POST");

export const ListPairedDevicesRequestConfig = new RequestBuilder({
  path: () => "/chief/paired",
  responseSchema: PairedDevicesListSchema,
});

export const RevokeDeviceRequestConfig = new RequestBuilder({
  paramsSchema: DeviceIdParamsSchema,
  path: ({ deviceId }) => `/chief/device/${deviceId}`,
  responseSchema: RevokedDeviceSchema,
})
  .withMethod("DELETE")
  .withContentType(undefined);

export const GetPairingIntentRequestConfig = new RequestBuilder({
  paramsSchema: PairingIntentIdParamsSchema,
  path: ({ intentId }) => `/chief/pairing-intents/${intentId}`,
  responseSchema: PairingIntentSchema,
}).withContentType(undefined);

export const ApprovePairingIntentRequestConfig = new RequestBuilder({
  paramsSchema: PairingIntentIdParamsSchema,
  path: ({ intentId }) => `/chief/pairing-intents/${intentId}/approve`,
  responseSchema: PairingIntentSchema,
})
  .withMethod("POST")
  .withContentType(undefined);

// POST /chief/sessions/start-for-notion-page - Find or create the session
// bound to a Notion meeting page. Org-scoped: the backend resolves the project.
export const StartForNotionPageRequestConfig = new RequestBuilder({
  path: () => "/chief/sessions/start-for-notion-page",
  bodySchema: StartForNotionPageBodySchema,
  responseSchema: StartForNotionPageResponseSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

export const ListChiefSessionsRequestConfig = new RequestBuilder({
  paramsSchema: ListChiefSessionsParamsSchema,
  path: () => "/chief/sessions",
  responseSchema: ListChiefSessionsResponseSchema,
})
  .withQueryParams((params) => {
    const qs: Record<string, string> = {};
    if (params?.limit !== undefined) qs.limit = String(params.limit);
    if (params?.afterId !== undefined) qs.after_id = params.afterId;
    if (params?.beforeId !== undefined) qs.before_id = params.beforeId;
    return qs;
  })
  .withIncludeProjectId();

// POST /chief/sessions-move - Reassign finished sessions to another project.
// Project-scoped on the source; the destination is authorized server-side, so a
// caller without create rights there gets a 404 rather than a partial move.
export const MoveChiefSessionsRequestConfig = new RequestBuilder({
  path: () => "/chief/sessions-move",
  bodySchema: MoveChiefSessionsBodySchema,
  responseSchema: MoveChiefSessionsResponseSchema,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

// GET /chief/tags — the project's tag vocabulary, so the live-session page can
// resolve the tag ids on a session's content into names and colours. Read-only
// on this surface: tags are created and applied in the Chief desktop app.
export const ListChiefTagsRequestConfig = new RequestBuilder({
  path: () => "/chief/tags",
  responseSchema: ListChiefTagsResponseSchema,
}).withIncludeProjectId();

// GET /chief/tagged — every tagged point across the project's recent sessions,
// for the Tags page.
export const ListChiefTaggedItemsRequestConfig = new RequestBuilder({
  path: () => "/chief/tagged",
  responseSchema: ChiefTaggedItemsResponseSchema,
}).withIncludeProjectId();

// GET /chief/session/{sessionId}/locate - Resolve a project-less session link
// (the one a calendar invite carries) to the project it lives in now.
// User-scoped: the link names no project to send.
export const LocateChiefSessionRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionIdParamsSchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}/locate`,
  responseSchema: ChiefSessionLocationSchema,
}).withContentType(undefined);

export const GetChiefSessionRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionIdParamsSchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}`,
  responseSchema: ChiefSessionSchema,
}).withIncludeProjectId();

export const DeleteChiefSessionRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionIdParamsSchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}`,
})
  .withMethod("DELETE")
  .withContentType(undefined)
  .withIncludeProjectId();

export const UpdateChiefSessionNameRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionIdParamsSchema,
  bodySchema: UpdateChiefSessionNameBodySchema,
  responseSchema: ChiefSessionNameResponseSchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}/name`,
})
  .withMethod("PUT")
  .withContentType("application/json")
  .withIncludeProjectId();

export const RestartChiefSessionRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionIdParamsSchema,
  responseSchema: ChiefSessionSchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}/restart`,
})
  .withMethod("POST")
  .withIncludeProjectId();

// Share link management (project members only). POST supports ?regenerate=true
// via the params-less query below when a fresh token should replace the
// current one.
export const CreateChiefSessionShareRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionIdParamsSchema,
  bodySchema: ChiefSessionShareBodySchema,
  responseSchema: ChiefSessionShareStatusSchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}/share`,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

export const GetChiefSessionShareStatusRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionShareParamsSchema,
  responseSchema: ChiefSessionShareStatusSchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}/share`,
})
  .withQueryParams((params) => (params ? { scope: params.scope } : undefined))
  .withIncludeProjectId();

export const RevokeChiefSessionShareRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionShareParamsSchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}/share`,
})
  .withQueryParams((params) => (params ? { scope: params.scope } : undefined))
  .withMethod("DELETE")
  .withContentType(undefined)
  .withIncludeProjectId();

export const SendChiefSessionShareEmailsRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionIdParamsSchema,
  bodySchema: ChiefShareEmailsBodySchema,
  responseSchema: ChiefShareEmailsResponseSchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}/share/email`,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

// Guest snapshot of a shared session (no auth required — the token is the
// authorization).
export const GetPublicChiefSessionRequestConfig = new RequestBuilder({
  paramsSchema: ChiefShareTokenParamsSchema,
  responseSchema: PublicChiefSessionSchema,
  path: ({ token }) => `/public-live-sessions/${token}`,
})
  .withMethod("GET")
  .withContentType(undefined);

// Guest view of a shared session document, and the image resolution behind it.
// Both are unauthenticated — the token is the authorization — and the image
// route still proves each asset has a live placement in this document.
export const GetPublicChiefArtifactRequestConfig = new RequestBuilder({
  paramsSchema: ChiefShareTokenParamsSchema,
  responseSchema: PublicChiefArtifactSchema,
  path: ({ token }) => `/public-artifacts/${token}`,
})
  .withMethod("GET")
  .withContentType(undefined);

export const PublicChiefArtifactImagesRequestConfig = new RequestBuilder({
  paramsSchema: ChiefShareTokenParamsSchema,
  bodySchema: PublicChiefArtifactImagesBodySchema,
  responseSchema: PublicChiefArtifactImagesResponseSchema,
  path: ({ token }) => `/public-artifacts/${token}/images/signed-urls`,
})
  .withMethod("POST")
  .withContentType("application/json");

export const AddChiefSessionChatRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionIdParamsSchema,
  bodySchema: AddChiefSessionChatBodySchema,
  responseSchema: ChiefSessionSchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}/chats`,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

// GET /chief/session/{sessionId}/media — inline transcript images, pinned
// text, and documents for the finished live-session page (shares storage with
// the Chief device). Read-only: items are added and removed on the device,
// during the live session. Pasted text and documents are asked for explicitly
// because the route withholds each from bundles built before this schema made
// the image-only fields optional — one such item would otherwise fail the
// whole list's parse.
export const ListChiefSessionMediaRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionIdParamsSchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}/media`,
  responseSchema: ListChiefSessionMediaResponseSchema,
})
  .withQueryParams(() => ({ pasted_text: "1", documents: "1" }))
  .withIncludeProjectId();

// POST /chief/session/{sessionId}/media/publish — publish inline transcript
// images to durable public links. The server only publishes assets actually
// linked to the session and silently skips the rest, so the response may
// legitimately contain fewer items than requested.
export const PublishChiefSessionMediaRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionIdParamsSchema,
  bodySchema: PublishChiefSessionMediaBodySchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}/media/publish`,
  responseSchema: PublishChiefSessionMediaResponseSchema,
})
  .withMethod("POST")
  .withIncludeProjectId();

// POST /chief/session/{sessionId}/suggested-prompts — asks the control plane to
// read the session and write send-to-agent instructions from it. The Chief
// desktop app reaches the same service on its device-token twin.
export const SuggestChiefSessionPromptsRequestConfig = new RequestBuilder({
  paramsSchema: ChiefSessionIdParamsSchema,
  bodySchema: SuggestChiefSessionPromptsBodySchema,
  path: ({ sessionId }) => `/chief/session/${sessionId}/suggested-prompts`,
  responseSchema: SuggestChiefSessionPromptsResponseSchema,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();
